import React from 'react';
import { Link } from 'react-router-dom';
import {
    FaCheckCircle, FaPlay, FaTrophy, FaChartLine, FaDatabase,
    FaChartPie, FaSearch, FaCreditCard, FaCar,
    FaShoppingCart, FaClock, FaArrowLeft, FaArrowRight,
    FaChartBar
} from 'react-icons/fa';
import ParticleBackground from '../common/ParticleBackground';

/**
 * CourseDashboard - Track-level dashboard for Thinking in Data & Data Viz
 */
const CourseDashboard = ({
    cases,
    completedCases,
    completedChallenges,
    onStartCase,
    onChallengeClick,
    completionPercentage,
    backLink = '/exercises',
    title = 'Thinking in Data',
    subtitle = 'Master data analysis through interactive business cases. Solve real-world SQL problems and build your analytical intuition.',
    accentColor = 'orange',   // 'blue' | 'orange'
}) => {
    const isBlue = accentColor !== 'orange';

    // Active-state accents - navy for blue track, single-tone amber for orange track
    const gradientFrom  = isBlue ? 'from-[#2d5a8f] to-[#1e3a5f]'         : 'from-amber-600 to-amber-500';
    const gradientText  = isBlue ? 'from-[#6b9fd4] via-[#4a7cbd] to-[#2d5a8f]' : 'from-[#d97706] via-[#b45309] to-[#92400e]';
    const iconBg        = isBlue ? 'from-[#2d5a8f] to-[#1e3a5f]'         : 'from-amber-600 to-amber-500';
    const iconShadow    = isBlue ? 'shadow-[#1e3a5f]/30'                  : 'shadow-amber-600/20';
    const hoverBorder   = isBlue ? 'dark:hover:border-[#2d5a8f]/50 hover:border-[#1e3a5f]/30'
                                 : 'dark:hover:border-amber-700/40 hover:border-amber-300/60';
    const glowLeft      = isBlue ? 'bg-[#1e3a5f]/[0.10] dark:bg-[#1e3a5f]/[0.08]'
                                 : 'bg-amber-500/[0.08] dark:bg-amber-600/[0.06]';
    const glowRight     = isBlue ? 'bg-[#0f1c2e]/[0.08] dark:bg-[#0f1c2e]/[0.06]'
                                 : 'bg-amber-400/[0.05] dark:bg-amber-400/[0.04]';

    // Completed-state - only the progress % text, the CTA button, and the Done badge change
    const doneProgressTxt = isBlue ? 'text-gray-400 dark:text-gray-500' : 'text-amber-600/70 dark:text-amber-500/60';
    const doneCta         = isBlue
        ? 'bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600'
        : 'bg-transparent text-amber-700/70 dark:text-amber-400/70 hover:text-amber-800 dark:hover:text-amber-300 border border-amber-200/70 dark:border-amber-800/30 hover:bg-amber-50/40 dark:hover:bg-amber-900/10';
    const doneBadge       = isBlue ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800/50'
                                   : 'text-amber-600/70 dark:text-amber-500/60 bg-amber-50 dark:bg-amber-900/20';

    const getCaseIcon = (c) => {
        // Data Viz - distinct icon per module
        if (c.id === 'data_viz_intro')         return <FaChartBar    size={18} />;
        if (c.id === 'data_viz_intermediate')  return <FaChartPie    size={18} />;
        if (c.id === 'data_viz_1')             return <FaSearch      size={18} />;
        if (c.id === 'data_viz_2')             return <FaCreditCard  size={18} />;
        if (c.id === 'data_viz_3')             return <FaCar         size={18} />;
        // SQL track fallbacks
        if (c.title?.includes('SQL'))          return <FaDatabase    size={18} />;
        if (c.category === 'Sales Analytics')  return <FaShoppingCart size={18} />;
        return <FaChartLine size={18} />;
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0a1220] flex flex-col font-sans selection:bg-orange-500/30 transition-colors duration-300">

            {/* ── Hero ── */}
            <div className="relative bg-white dark:bg-[#0a1220] border-b border-gray-200 dark:border-gray-800/80 pt-14 pb-24 px-4 lg:px-8 overflow-hidden">

                {/* Particle canvas */}
                <ParticleBackground particleCount={45} maxDist={105} speed={0.22} />

                {/* Glow blobs */}
                <div className={`absolute top-0 right-1/4 w-[500px] h-[280px] ${glowLeft} rounded-full blur-[100px] pointer-events-none`} />
                <div className={`absolute bottom-0 left-1/4 w-[380px] h-[200px] ${glowRight} rounded-full blur-[80px] pointer-events-none`} />

                <div className="relative max-w-7xl mx-auto">

                    {/* Back link */}
                    {backLink && (
                        <div className="mb-7">
                            <Link
                                to={backLink}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-400 hover:text-orange-400 transition-colors"
                            >
                                <FaArrowLeft size={9} />
                                All Courses
                            </Link>
                        </div>
                    )}

                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
                        <div className="max-w-2xl">
                            <h1 className="text-4xl md:text-[3.25rem] font-extrabold mb-4 text-gray-900 dark:text-white leading-[1.1] tracking-tight">
                                <span className={`bg-gradient-to-r ${gradientText} text-transparent bg-clip-text`}>
                                    {title}
                                </span>
                            </h1>
                            <p className="text-gray-500 dark:text-gray-400 text-base leading-relaxed">
                                {subtitle}
                            </p>
                        </div>

                        {/* Progress card */}
                        <div className="relative bg-white/80 dark:bg-[#0f1c2e]/80 backdrop-blur-md rounded-2xl p-6 border border-gray-200 dark:border-gray-700/60 min-w-[270px] shadow-lg">
                            <div className="flex items-center justify-between mb-3">
                                <span className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-white">
                                    <FaTrophy className="text-amber-400" size={14} />
                                    Course Progress
                                </span>
                                <span className="text-2xl font-extrabold tabular-nums text-gray-900 dark:text-white">{completionPercentage}%</span>
                            </div>
                            <div className="w-full bg-gray-100 dark:bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
                                <div
                                    className={`${isBlue ? 'bg-[#1e3a5f] dark:bg-[#4a7cbd]' : 'bg-amber-500'} h-full rounded-full`}
                                    style={{ width: `${completionPercentage}%` }}
                                />
                            </div>
                            <div className="mt-2.5 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                                {completedCases.size} of {cases.length} modules complete
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Module Cards ── */}
            <div className="flex-1 px-4 lg:px-8 -mt-10 pb-20">
                <div className="max-w-7xl mx-auto space-y-5">
                    {cases.map((caseItem, index) => {
                        const caseKey = `case_${caseItem.id}`;
                        const caseCompletedChallenges = completedChallenges[caseKey] || [];
                        const isCaseCompleted = completedCases.has(caseItem.id);
                        const progressPercent = caseItem.challengeCount > 0
                            ? Math.min(100, Math.round((caseCompletedChallenges.length / caseItem.challengeCount) * 100))
                            : 0;

                        return (
                            <div
                                key={caseItem.id}
                                className={`relative flex flex-col md:flex-row bg-white dark:bg-[#0a1525] rounded-2xl overflow-hidden border shadow-md transition-all duration-300 border-gray-200 dark:border-gray-800/80 hover:shadow-2xl ${hoverBorder}`}
                            >
                                {/* Coloured top band */}
                                <div className={`absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r ${gradientFrom}`} />

                                {/* Ambient glow corner */}
                                <div className="absolute -bottom-8 -left-8 w-36 h-36 bg-[#1e3a5f]/10 rounded-full blur-2xl pointer-events-none" />

                                {/* ── Left: Module Info ── */}
                                <div className="relative p-7 md:w-5/12 border-b md:border-b-0 md:border-r border-gray-100 dark:border-gray-800/80 bg-gray-50/50 dark:bg-[#0a1525] flex flex-col gap-5 mt-[3px]">

                                    {/* Icon + title */}
                                    <div className="flex items-start gap-4">
                                        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br ${iconBg} ${iconShadow} text-white`}>
                                            {getCaseIcon(caseItem)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">
                                                Module {index + 1}
                                            </div>
                                            <h3 className="text-lg font-extrabold text-gray-900 dark:text-white leading-tight mt-0.5">
                                                {caseItem.title}
                                            </h3>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed flex-1">
                                        {caseItem.cardDescription || caseItem.scenario}
                                    </p>

                                    {/* Meta */}
                                    <div className="flex items-center gap-3 text-[11px] text-gray-400 dark:text-gray-500">
                                        {caseItem.duration && (
                                            <span className="flex items-center gap-1"><FaClock size={9} />{caseItem.duration}</span>
                                        )}
                                        {caseItem.badge && (
                                            <span className="font-bold text-[10px] px-2 py-0.5 rounded-full bg-[#1e3a5f]/5 dark:bg-[#2d5a8f]/20 text-[#1e3a5f] dark:text-[#93c5fd] border border-[#1e3a5f]/20 dark:border-[#2d5a8f]/40">{caseItem.badge}</span>
                                        )}
                                        {caseItem.category && !caseItem.badge && (
                                            <span className="text-gray-400">{caseItem.category}</span>
                                        )}
                                    </div>

                                    {/* Progress bar */}
                                    <div>
                                        <div className="flex justify-between text-[10px] font-semibold text-gray-400 dark:text-gray-500 mb-1.5">
                                            <span>{caseCompletedChallenges.length} of {caseItem.challengeCount} challenges</span>
                                            <span className={isCaseCompleted ? doneProgressTxt : (isBlue ? 'text-[#1e3a5f] dark:text-[#4a7cbd]' : 'text-amber-600 dark:text-amber-500')}>
                                                {progressPercent}%
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-100 dark:bg-gray-700/50 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full ${isBlue ? 'bg-[#1e3a5f] dark:bg-[#4a7cbd]' : 'bg-amber-500'}`}
                                                style={{ width: `${progressPercent}%` }}
                                            />
                                        </div>
                                    </div>

                                    {/* CTA */}
                                    <button
                                        onClick={() => onStartCase(index)}
                                        className={`w-full py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                                            isCaseCompleted
                                                ? doneCta
                                                : isBlue
                                                    ? 'bg-[#1e3a5f] dark:bg-[#2d5a8f] text-white hover:bg-[#162d4a] dark:hover:bg-[#3a6ba5] shadow-sm'
                                                    : 'bg-amber-600 text-white hover:bg-amber-700 shadow-sm shadow-amber-600/20'
                                        }`}
                                    >
                                        {isCaseCompleted ? (
                                            <>Review Module <FaCheckCircle size={11} /></>
                                        ) : progressPercent > 0 ? (
                                            <>Continue ({progressPercent}%) <FaArrowRight size={11} /></>
                                        ) : (
                                            <>{caseItem.ctaLabel || 'Start Module'} <FaPlay size={9} /></>
                                        )}
                                    </button>
                                </div>

                                {/* ── Right: Challenge Syllabus ── */}
                                <div className="relative p-7 md:w-7/12 bg-white dark:bg-[#0a1525] mt-[3px] overflow-hidden">

                                    {/* Header */}
                                    <div className="flex justify-between items-center mb-5">
                                        <h4 className="font-bold text-gray-800 dark:text-white flex items-center gap-2 text-sm">
                                            <span className={`w-2 h-2 rounded-full bg-gradient-to-r ${gradientFrom}`} />
                                            Curriculum
                                        </h4>
                                        <div className="flex items-center gap-2.5">
                                            {caseItem.duration && (
                                                <span className="text-[11px] font-medium text-gray-400 flex items-center gap-1">
                                                    <FaClock size={9} /> {caseItem.duration}
                                                </span>
                                            )}
                                            <span className="text-[10px] font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800/60 px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700/50">
                                                {caseCompletedChallenges.length} / {caseItem.challenges ? caseItem.challenges.length : caseItem.challengeCount}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Challenge list */}
                                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                                        {caseItem.challenges && caseItem.challenges.map((challenge, i) => {
                                            const isDone = caseCompletedChallenges.includes(challenge.id);
                                            return (
                                                <button
                                                    key={challenge.id}
                                                    onClick={() => onChallengeClick(index, i)}
                                                    className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all duration-150 group bg-gray-50 dark:bg-gray-800/30 border-gray-100 dark:border-gray-800/60 hover:bg-white dark:hover:bg-gray-800/60 hover:border-gray-200 dark:hover:border-gray-700 hover:shadow-sm"
                                                >
                                                    {/* Number badge */}
                                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold border transition-colors ${
                                                        isBlue
                                                            ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 group-hover:border-[#2d5a8f] group-hover:text-[#1e3a5f] dark:group-hover:border-[#4a7cbd] dark:group-hover:text-[#93c5fd]'
                                                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-400 group-hover:border-amber-400 group-hover:text-amber-600 dark:group-hover:border-amber-600 dark:group-hover:text-amber-400'
                                                    }`}>
                                                        {i + 1}
                                                    </div>

                                                    <div className="flex-1 min-w-0 flex flex-col">
                                                        <p className="text-[13px] font-medium truncate transition-colors text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                                                            {challenge.title.replace(/^Q\d+[.:]\s*/i, '')}
                                                        </p>
                                                        {challenge.expectedChartType && (
                                                            <span className="text-[10px] text-gray-400 dark:text-gray-600 font-medium leading-tight">
                                                                {({'bar':'Bar','stacked-bar':'Stacked Bar','line':'Line','area':'Area','pie':'Pie','histogram':'Histogram','heatmap':'Heatmap','map':'Map','boxplot':'Box Plot','scatter':'Scatter','dual-axis':'Dual Axis','combo':'Combo','metric':'Metric','treemap':'Treemap'})[challenge.expectedChartType] || challenge.expectedChartType}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {isDone ? (
                                                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${doneBadge}`}>
                                                            Done
                                                        </span>
                                                    ) : (
                                                        <FaArrowRight size={9} className="text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-400 flex-shrink-0 transition-colors" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                        {(!caseItem.challenges || caseItem.challenges.length === 0) && (
                                            <p className="text-sm text-gray-400 italic">Challenges loading…</p>
                                        )}
                                    </div>

                                    {/* Fade at bottom */}
                                    <div className="absolute bottom-0 left-0 w-full h-10 bg-gradient-to-t from-white dark:from-[#0a1525] to-transparent pointer-events-none md:rounded-br-2xl" />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default CourseDashboard;

